import { forwardRef, type TextareaHTMLAttributes } from 'react';
import { type LucideIcon } from 'lucide-react';

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  icon?: LucideIcon;
  error?: string;
  containerClassName?: string;
  hint?: string;
  isDisabled?: boolean;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ label, icon: Icon, error, hint, className = '', containerClassName = '', isDisabled, ...props }, ref) => {
    return (
      <div className={`space-y-2 ${containerClassName}`}>
        {label && <label className="mb-2 block nav-label text-gray-700">{label}</label>}
        <div className="relative group">
          {Icon && (
            <div className="absolute left-4 top-4 text-gray-400 group-focus-within:text-lime-900 transition-colors">
              <Icon size={18} strokeWidth={2.5} />
            </div>
          )}

          <textarea
            ref={ref}
            className={`
              w-full rounded-2xl border bg-white text-sm text-gray-900 placeholder:text-gray-400
              shadow-sm outline-none transition-all duration-200
              ${Icon ? 'pl-11' : 'pl-4'} 
              pr-4
              py-[13.5px]
              min-h-[120px]
              ${
                error
                  ? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-500/5'
                  : 'border-gray-200 group-hover:border-gray-300 focus:border-lime-900 focus:ring-4 focus:ring-lime-900/5'
              }
              disabled:opacity-50 disabled:cursor-not-allowed
              ${className}
            `}
            disabled={isDisabled ?? props.disabled}
            {...props}
          />
        </div>
        {error && <p className="px-1 text-xs font-bold text-red-600 animate-in fade-in slide-in-from-top-1">{error}</p>}
        {hint && !error && <p className="px-1 desc-sm italic leading-relaxed">{hint}</p>}
      </div>
    );
  }
);

TextArea.displayName = 'TextArea';
